import { useTranslation } from 'next-i18next'
import { useEffect, useRef } from 'react'

import { Modal } from '@src/quince-components/src/atoms/modal'
import {
  ReviewSubmissionContainer,
  type ReviewSubmissionEntryContext,
  type TRecentPurchaseItem,
} from '@src/quince-components/src/atoms/reviews/reviewSubmission'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { useReviewsPageContentQuery } from '@src/quince-site-common/src/services/reviewService'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'

import styles from './post-order-review-modal.module.scss'
import { buildAppReviewSubmitUrl } from './post-order-review.helpers'

interface PostOrderReviewModalProps {
  entryOverride?: ReviewSubmissionEntryContext
  isWebView: boolean
  onNativeAppHandOff: () => void
  onClose: () => void
  onFirstStepSuccess?: () => void
  onDone: () => void
  onWriteReviewItem: (item: TRecentPurchaseItem) => void
}

export const PostOrderReviewModal = ({
  entryOverride,
  isWebView,
  onNativeAppHandOff,
  onClose,
  onFirstStepSuccess,
  onDone,
  onWriteReviewItem,
}: PostOrderReviewModalProps) => {
  const { t } = useTranslation('common')
  const canHandOffToNativeApp =
    isWebView && typeof window !== 'undefined' && typeof window.ReactNativeWebView?.postMessage === 'function'
  const { data: reviewsPageContent, isLoading: isReviewsPageContentLoading } =
    useReviewsPageContentQuery(!canHandOffToNativeApp)

  const hasHandedOffRef = useRef(false)
  useEffect(() => {
    if (
      !canHandOffToNativeApp ||
      hasHandedOffRef.current ||
      !entryOverride?.quinceProductId ||
      !entryOverride.orderNumber
    ) {
      return
    }
    hasHandedOffRef.current = true
    try {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          // @i18n-ignore
          action: 'NAVIGATION',
          nextURL: buildAppReviewSubmitUrl({
            quinceProductId: entryOverride.quinceProductId,
            variantId: entryOverride.variantId,
            orderNumber: entryOverride.orderNumber,
            customerId: entryOverride.customerId,
            fulfillmentId: entryOverride.fulfillmentId,
            author: entryOverride.author,
            source: entryOverride.source,
          }),
        }),
      )
    } catch (error) {
      captureException(error, { module: MODULES.POST_ORDER_WEB, feature: FEATURES.ORDER_DETAILS })
    }
    onNativeAppHandOff()
  }, [canHandOffToNativeApp, entryOverride, onNativeAppHandOff])

  if (canHandOffToNativeApp) return null

  return (
    <Modal
      open
      onClose={onClose}
      showCloseButton
      className={styles.reviewSubmissionModal}
      closeButtonContainerClassName={styles.reviewSubmissionCloseButton}
      aria-label={t('write_a_review')}
    >
      <ReviewSubmissionContainer
        pageDataJson={reviewsPageContent ?? undefined}
        isPageDataLoading={isReviewsPageContentLoading}
        onFirstStepSuccess={onFirstStepSuccess}
        onDone={onDone}
        onWriteReviewItem={onWriteReviewItem}
        entryContext={entryOverride}
      />
    </Modal>
  )
}

export default PostOrderReviewModal
